<script setup>
/**
 * 編輯問題元件 (EditQuestion.vue)
 *
 * `QusDetail` 的子元件，載入問題分類後，讓作者修改問題標題、分類與 Quill 內容並送出更新。
 *
 * @fires closeEdit - 關閉編輯（傳入是否需要重新載入）
 */
import questionsAPI from '../../apis/questions';
import {
  ref, onMounted, defineEmits, defineProps,
} from 'vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';



// 因為改變 delimiters 出現的問題
// eslint-disable-next-line no-unused-vars
import { transformTypeToId, questionType } from '../../utils/format';
// eslint-disable-next-line no-unused-vars
import { toolbarOptions, modulesOptions } from '../../plugins/quill';

const emit = defineEmits(['closeEdit']);
const props = defineProps(['data']);
const isLoading = ref(true);
const editor = ref(null);

const questionTypesList = ref({});
/**
 * 取得問題分類清單並寫入 `questionTypesList`。
 */
const getQuestionCategoryMenu = () => {
  questionsAPI.apiGetQuestionTypes().then((res) => {
    questionTypesList.value = res.data.question_types;
    isLoading.value = false;
  }).catch((error) => {
    isLoading.value = false;
    postMessage('danger', '取得問題分類時發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  })
};

const editQuestion = ref({
  title: props.data.title,
  question_type_id: transformTypeToId(props.data.question_type_name),
  body: props.data.body,
});

/**
 * 限制 `editQuestion` 指定欄位長度，超過則截斷。
 * @param {'title'} key - `editQuestion` 的欄位名稱
 * @param {number} limit - 上限字數
 */
const limitLength = (key, limit) => {
  if (editQuestion.value[key].length > limit) {
    editQuestion.value[key] = editQuestion.value[key].slice(0, limit);
  }
}

/**
 * 送出問題編輯；內容超過 16800 字則中止並提示。成功後 emit `closeEdit(true)`。
 */
const submitEditQuestion = () => {
  isLoading.value = true;
  if (editQuestion.value.body.length > 16800) {
    alert('問題內容過長（限制 16800 字），請刪除部分文字');
    isLoading.value = false;
    return;
  } else {
    questionsAPI.apiPutQuestion(props.data.id, editQuestion.value).then(() => {
      isLoading.value = false;
      emit('closeEdit', true);
    }).catch((error) => {
      isLoading.value = false;
      postMessage('danger', '編輯問題時發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
    })
  }
};
onMounted(() => {
  getQuestionCategoryMenu();
});
</script>

<template>
  <div class="">
    <div class="position-relative">
      <loading :active="isLoading" :is-full-page="false" />
      <div class="">
        <h4 class="mb-0">
          編輯問題
        </h4>
      </div>
      <div class="my-3">
        <label for="title" class="form-label">問題標題 <small>(50 字內)</small></label>
        <input id="title" v-model="editQuestion.title" type="text" class="form-control" aria-describedby="" :class="!editQuestion.title && 'is-invalid'" @blur="limitLength('title', 50)" @keydown="limitLength('title', 50)" placeholder="e.g. 作業繳交">
        <div class="invalid-feedback" :class="!editQuestion.title && 'd-block'">
          問題標題為必填
        </div>
      </div>
      <div class="mb-3">
        <label for="category" class="form-label">問題分類</label>
        <select id="category" v-model="editQuestion.question_type_id" class="form-select"
          aria-label="Default select example">
          <option v-for="qusType in questionTypesList" :key="qusType.name" :value="qusType.id">
            ${ questionType(qusType.name) }
          </option>
        </select>
      </div>
      <div class="mt-3">
        <QuillEditor ref="editor" v-model:content="editQuestion.body" theme="snow" content-type="html"
          :toolbar="toolbarOptions" :modules="modulesOptions" />
        <div class="my-3 d-flex justify-content-between">
          <button class="btn btn-light px-4" @click="$emit('closeEdit', false)">
            取消
          </button>
          <button class="btn btn-hex-primary px-4" :disabled="editQuestion.title === '' || isLoading"
            @click="submitEditQuestion">
            送出
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>

<spec lang="md">
# 編輯問題 (EditQuestion.vue)

## 元件概述
- **用途**：`QusDetail` 的子元件，讓作者編輯既有問題的標題、分類與內容並送出更新。

## Props
| 名稱 | 類型 | 必填 | 預設值 | 說明 |
|-----|------|-----|--------|------|
| data | `object` | 是 | - | 欲編輯的問題物件 |
| data.id | `number` | 是 | - | 問題 id |
| data.title | `string` | 是 | - | 問題標題 |
| data.question_type_name | `string` | 是 | - | 問題分類名稱（經 `transformTypeToId` 轉為 id） |
| data.body | `string` | 是 | - | 問題內容（HTML） |

## Emits
| 事件名稱 | 參數 | 說明 |
|---------|-----|------|
| closeEdit | `(refresh: boolean)` | 關閉編輯；`true` 表示需重新載入問題 |

## Slots
無

## 功能行為
- `onMounted` 載入問題分類清單（`getQuestionCategoryMenu`）
- 以 `props.data` 初始化 `editQuestion`，分類名稱經 `transformTypeToId` 轉為 id
- `limitLength` 於 blur / keydown 時限制標題 50 字
- `submitEditQuestion` 內容超過 16800 字則 `alert` 中止；成功後 emit `closeEdit(true)`
- 「取消」按鈕 emit `closeEdit(false)`

## 測試行為
- 應以 `data` 初始化標題、分類與內容欄位
- 標題為空時，輸入框應套用 `is-invalid` 並顯示「問題標題為必填」
- 標題超過 50 字時，應截斷為 50 字
- 標題為空或載入中時，送出按鈕應禁用
- 內容超過 16800 字時，應 `alert` 並中止送出
- 送出成功時，應 emit `closeEdit` 並帶 `true`
- 點擊「取消」時，應 emit `closeEdit` 並帶 `false`

## UI 規格
- 標題「編輯問題」
- 使用 `QuillEditor`（snow 主題、html 內容）編輯問題內容
- 載入時以 `vue-loading-overlay` 顯示區塊級 Loading（`:is-full-page="false"`）
- 底部為「取消」（btn-light）與「送出」（btn-hex-primary）按鈕
</spec>
